import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stack,
} from '@mui/material';
import {
  People,
  SportsTennis,
  EmojiEvents,
  Schedule,
  Business,
  LocationOn,
  Score,
  TrendingUp,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts';
import { useAdminStore } from '../../stores/adminStore';
import { StatsCard } from '../../components/admin/StatsCard';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { useNavigate } from 'react-router-dom';

export const AdminDashboardPage: React.FC = () => {
  const { stats, period, loading, error, setStats, setPeriod, setLoading, setError } = useAdminStore();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.is_admin) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, user]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAdminStats(period);
      setStats(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'match_created':
        return <SportsTennis />;
      case 'user_registered':
        return <People />;
      case 'tournament_started':
        return <EmojiEvents />;
      default:
        return <Schedule />;
    }
  };

  if (!user?.is_admin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You need admin privileges to access this section.</Typography>
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Loading admin dashboard..." />;
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const matchStatusData = stats ? [
    { name: 'Active', value: stats.active_matches, color: '#4caf50' },
    { name: 'Completed', value: stats.completed_matches, color: '#2196f3' },
    { name: 'Pending', value: stats.pending_matches, color: '#ff9800' },
  ] : [];

  const COLORS = ['#4caf50', '#2196f3', '#ff9800'];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
            Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user?.name}. Here's your system overview.
          </Typography>
        </Box>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Time Period</InputLabel>
          <Select
            value={period}
            label="Time Period"
            onChange={(e) => setPeriod(e.target.value as 'week' | 'month' | 'all_time')}
          >
            <MenuItem value="week">Last 7 Days</MenuItem>
            <MenuItem value="month">Last 30 Days</MenuItem>
            <MenuItem value="all_time">All Time</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' }, minWidth: 250 }}>
          <StatsCard
            title="Total Users"
            value={stats?.total_users || 0}
            icon={<People />}
            color="primary"
            subtitle={`${stats?.period_users || 0} this period`}
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' }, minWidth: 250 }}>
          <StatsCard
            title="Total Matches"
            value={stats?.total_matches || 0}
            icon={<SportsTennis />}
            color="success"
            subtitle={`${stats?.period_matches || 0} this period`}
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' }, minWidth: 250 }}>
          <StatsCard
            title="Active Matches"
            value={stats?.active_matches || 0}
            icon={<Schedule />}
            color="info"
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' }, minWidth: 250 }}>
          <StatsCard
            title="Tournaments"
            value={stats?.total_tournaments || 0}
            icon={<EmojiEvents />}
            color="warning"
          />
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' }, minWidth: 250 }}>
          <StatsCard
            title="Clubs"
            value={stats?.total_clubs || 0}
            icon={<Business />}
            color="primary"
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' }, minWidth: 250 }}>
          <StatsCard
            title="Courts"
            value={stats?.total_courts || 0}
            icon={<LocationOn />}
            color="info"
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' }, minWidth: 250 }}>
          <StatsCard
            title="Scores"
            value={stats?.total_scores || 0}
            icon={<Score />}
            color="success"
          />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' }, minWidth: 250 }}>
          <StatsCard
            title="Avg Win Rate"
            value={`${stats?.average_win_rate || 0}%`}
            icon={<TrendingUp />}
            color="warning"
          />
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 3 }}>
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Match Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={matchStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {matchStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Recent Activity
              </Typography>
              <List>
                {stats?.recent_activity?.slice(0, 5).map((activity, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {getActivityIcon(activity.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.details || `${activity.type.replace('_', ' ')} by ${activity.user}`}
                      secondary={new Date(activity.time).toLocaleString()}
                    />
                  </ListItem>
                ))}
                {(!stats?.recent_activity || stats.recent_activity.length === 0) && (
                  <ListItem>
                    <ListItemText primary="No recent activity" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Quick Actions
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 200 }}>
              <Button
                variant="outlined"
                startIcon={<People />}
                fullWidth
                onClick={() => navigate('/admin/users')}
              >
                Manage Users
              </Button>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 200 }}>
              <Button
                variant="outlined"
                startIcon={<SportsTennis />}
                fullWidth
                onClick={() => navigate('/admin/matches')}
              >
                Manage Matches
              </Button>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 200 }}>
              <Button
                variant="outlined"
                startIcon={<EmojiEvents />}
                fullWidth
                onClick={() => navigate('/admin/tournaments')}
              >
                Manage Tournaments
              </Button>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 200 }}>
              <Button
                variant="outlined"
                startIcon={<Business />}
                fullWidth
                onClick={() => navigate('/admin/clubs')}
              >
                Manage Clubs
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
